import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";

// Array of small duas
const duas = [
  {
    arabic: "رَبِّ زِدْنِي عِلْمًا",
    pronunciation: "Rabbi zidni ilma",
    translation: "My Lord, increase me in knowledge"
  },
  {
    arabic: "اللهم انفعني بما علمتني",
    pronunciation: "Allahumma anfa‘ni bima ‘allamtani",
    translation: "O Allah, benefit me with what You have taught me"
  },
  {
    arabic: "اللهم اشرح لي صدري",
    pronunciation: "Allahumma ishrah li sadri",
    translation: "O Allah, expand my chest (grant me confidence)"
  },
];

// Comprehensive lessons array
const lessons = [
  {
    title: "Alphabet",
    link: "/lessons/alphabet",
    color: "bg-yellow-200",
    emoji: "🔤"
  },
  {
    title: "Long Vowels",
    link: "/lessons/longvowels",
    color: "bg-pink-200",
    emoji: "🅰️"
  },
  {
    title: "Animals",
    link: "/lessons/animals",
    color: "bg-green-200",
    emoji: "🐪"
  },
  {
    title: "Words",
    link: "/lessons/words",
    color: "bg-blue-200",
    emoji: "📝"
  },
  {
    title: "Chakl (Harakat)",
    link: "/lessons/chakl",
    color: "bg-purple-200",
    emoji: "ًٌٍَُِّْ"
  },
  {
    title: "Joint Letters",
    link: "/lessons/joint",
    color: "bg-orange-200",
    emoji: "🔗"
  },
  {
    title: "Conversation",
    link: "/lessons/conversation",
    color: "bg-teal-200",
    emoji: "💬"
  },
  {
    title: "Surahs",
    link: "/lessons/surahs",
    color: "bg-indigo-200",
    emoji: "📖"
  },
  {
    title: "Prayer Lessons",
    link: "/lessons/prayers",
    color: "bg-red-200",
    emoji: "🕌"
  },
  {
    title: "Kids Songs",
    link: "/songs",
    color: "bg-pink-100",
    emoji: "🎵"
  },
  {
    title: "Prophet Stories",
    link: "/prophet-stories",
    color: "bg-yellow-100",
    emoji: "🌟"
  },
  {
    title: "Prayer Steps",
    link: "/prayer/steps",
    color: "bg-green-100",
    emoji: "🧎"
  },
  {
    title: "Female Prayer Steps",
    link: "/prayer/steps/female",
    color: "bg-purple-100",
    emoji: "🧕"
  },
  {
    title: "Alphabet Game",
    link: "/game/alphabet",
    color: "bg-blue-100",
    emoji: "🎲"
  },
  {
    title: "Flashcards Game",
    link: "/game/flashcards",
    color: "bg-yellow-100",
    emoji: "🃏"
  },
  {
    title: "Word Match Game",
    link: "/game/word-match",
    color: "bg-pink-100",
    emoji: "🧩"
  },
  {
    title: "Games",
    link: "/game",
    color: "bg-orange-100",
    emoji: "🎮"
  },
];

export default function LessonsPage() {
  // Use a media query to detect mobile
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isMobile) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-b from-yellow-50 to-pink-50">
        {/* Duas Swiper with arrows, no dots */}
        <div className="relative w-full max-w-md mb-2" style={{ minHeight: "110px" }}>
          <Swiper
            loop={true}
            spaceBetween={10}
            slidesPerView={1}
            navigation={true}
            modules={[Navigation]}
            className="w-full"
            style={{ minHeight: "110px" }}
          >
            {duas.map((dua, idx) => (
              <SwiperSlide key={idx}>
                <div className="w-full text-center py-2 px-2 bg-white/90 rounded-xl shadow mb-2">
                  <div className="text-lg font-bold text-pink-600 mb-1">{dua.arabic}</div>
                  <div className="text-base text-yellow-700 mb-1">{dua.pronunciation}</div>
                  <div className="text-xs text-gray-600">{dua.translation}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <style>
            {`
              .swiper-button-next, .swiper-button-prev {
                color: #f472b6;
                top: 50%;
                width: 32px;
                height: 32px;
                background: rgba(255,255,255,0.8);
                border-radius: 9999px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                transform: translateY(-50%);
              }
              .swiper-button-next {
                right: 0;
              }
              .swiper-button-prev {
                left: 0;
              }
              .swiper-button-next:after, .swiper-button-prev:after {
                font-size: 20px;
                font-weight: bold;
              }
            `}
          </style>
        </div>
        {/* Lessons Swiper */}
        <Swiper
          loop={true}
          spaceBetween={30}
          slidesPerView={1}
          className="w-full h-[80vh]"
        >
          {lessons.map((lesson, idx) => (
            <SwiperSlide key={idx}>
              <Link
                to={lesson.link}
                className={`flex flex-col items-center justify-center h-[70vh] w-11/12 mx-auto rounded-3xl shadow-2xl text-center text-4xl font-bold text-yellow-700 ${lesson.color} active:scale-95 transition`}
                style={{
                  minHeight: "400px",
                  maxWidth: "500px",
                  marginTop: "5vh",
                  marginBottom: "5vh",
                }}
              >
                <span className="text-7xl block mb-6">{lesson.emoji}</span>
                {lesson.title}
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  // Desktop: grid/list with header
  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6 text-center">Lessons</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {lessons.map((lesson, idx) => (
          <Link
            key={idx}
            to={lesson.link}
            className={`rounded-xl shadow-lg p-6 text-center text-xl font-bold text-yellow-700 hover:scale-105 transition ${lesson.color}`}
          >
            <span className="text-4xl block mb-2">{lesson.emoji}</span>
            {lesson.title}
          </Link>
        ))}
      </div>
    </div>
  );
}